import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import InfiniteScroll from "src/components/infiniteScroll";
import { CoursesLearnersList } from "./learners";
import { EditCourseReportsDetail } from "./detail";
import AssessmentReportView from "./assessmentReport";
import { UserGroupsFilter } from "../learners/filters/UserGroupsFilter";

import { fetchCourseLearners } from "src/redux/actions/coaching/learners";
import { CallRecording } from "./callRecording";
import { SimulationReportView } from "./simulationReportView";
import {
  fetchDraftCourse,
  fetchPublishedCourse,
  invalidateLearnersAssesmentReport,
} from "src/redux/actions/coaching/courses";
import { fetchTranscriptsForSimulation } from "src/redux/actions/coaching/simulation";

import { CourseLearnerAttempt, LearningRecordCourse, SimulationAttempt, SimulationSession } from "src/types/coaching/courses";
import { FetchCourseLearnersRequest } from "src/types/coaching/learners";
import { UserGroup } from "src/types/users";
import { CallRecordingState, CourseContext, CourseContextProps, SimulationReportState } from "../../context";

import { flattenContents } from "../../helper";
import userRoleService from "src/utils/userRoles.service";
import {
  COACHING_REPORTS_PATH,
  getManageNavigationFrom,
  getManageNavigationSelectedUserGroups,
  getManageReportsBackLabel,
  navigateManageLearnerReportsBack,
} from "src/pages/coaching/utils/manageNavigation";

import _ from "lodash";
import cx from "classnames";
import styles from "./reports.module.scss";

 const EditCoursesReportsPage: React.FC<object> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
  
    const { id: courseId, learningId } = useParams();
    const navigationFrom = getManageNavigationFrom(location.state);

    const [selectedUserGroups, setSelectedUserGroups] = useState<UserGroup[]>(
      () => getManageNavigationSelectedUserGroups(location.state) ?? [],
    );

    const loading = useSelector((state: any) => {
      return _.get(state.courses, `attemptLearners.loading`);
    });
  
    const learnersCourseRecord = useSelector((state: any) => {
      return _.get(state.courses, `learnerReport[${learningId}].loading`);
    });

    const {
      learnersLoading,
      learnersLoaded,
      learners,
      totalRecordsCount,
      currentPage,
      nextPage,
      hasMoreItems,
    } = useSelector((state: any) => {
      const learnersState = _.get(state.courses, "learners") || {};
      const loading = _.get(learnersState, "loading");
      const loaded = _.get(learnersState, "loaded");
      const pagination = _.get(learnersState, "pagination");
      const totalRecordsCount = _.get(pagination, "totalRecordsCount", 0);
      const currentPage = _.get(pagination, "currentPage", 0);
      const total = _.get(pagination, "totalPages", 0);
      const hasMoreItems = _.get(pagination, "hasMoreItems");
  
      const learners = _.get(learnersState, "items") || [];
  
      let nextPage: number | null = _.get(pagination, "currentPage", 0) + 1;
      if (nextPage && nextPage >= total) {
        nextPage = null;
      }
  
      return {
        learnersLoading: loading,
        learnersLoaded: loaded,
        learners: learners,
        totalRecordsCount: totalRecordsCount,
        currentPage: currentPage,
        nextPage: nextPage,
        hasMoreItems: hasMoreItems,
      };
    }, _.isEqual);

    const course: LearningRecordCourse = useSelector((state: any) =>
      _.get(state.courses, `learnerReport.${learningId}.item`)
    );

    const attemptLearners: CourseLearnerAttempt[] = useSelector(
      (state: any) =>
        _.get(state.courses, "attemptLearners.items", []) as CourseLearnerAttempt[]
    );

    const completedLearners = useMemo(
      () =>
        learners.filter((item: { status?: string }) => item.status === "COMPLETED"),
      [learners],
    );

    const learner = completedLearners.find((item: any) => item.id === learningId);

    const showReportsEmptyState =
      learnersLoaded && !learnersLoading && completedLearners.length === 0;

   const reportsEmptyDescription = useMemo(() => {
     if (_.size(selectedUserGroups) > 0) {
       const count = _.size(selectedUserGroups);
       const groupNames = selectedUserGroups.map((group) => group.name).join(", ");

       return `The selected user ${count > 1 ? 'groups' : 'group'}, ${groupNames}, ${count > 1 ? "don't" : "doesn't"} contain any learners. Choose a different group to view reports`;
     }

     return "No completed learners match the current filters. Try choosing another group.";
   }, [selectedUserGroups]);

    const [selectedAttempt, setSelectedAttempt] = useState<any>(null);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [showReport, setShowReport] = useState(false);
    const [callRecording, setCallRecording] = useState<CallRecordingState | null>(null);
    const [simulationReport, setSimulationReport] = useState<SimulationReportState | null>(null);

    const attempts : CourseLearnerAttempt[] = attemptLearners.map((item, index, arr) => {
      return {
        ...item,
        attemptNumber: arr.length - index, 
      };
    });

    const initialAttempt = _.last(attempts);

    const selectedSession = useSelector((state: any) =>
      _.get(state, `courses.learnerReport.${selectedAttempt?.id}.item.sessions`, [])
        .find((s: any) => s.id === selectedSessionId)
    );

    const childLearningRecord = useMemo(() => {
      return _.get(callRecording, "session.childLearningRecord", null);
    }, [callRecording]);

    const fetchingTranscripts = useSelector((state: any) => {
      if (!childLearningRecord) {
        return false;
      }
      return _.get(
        state.coaching,
        `simulations.transcripts.[${childLearningRecord}].fetching`
      );
    });
 
    const transcripts = useSelector((state: any) => {
      if (!childLearningRecord) {
        return null;
      }
      return _.get(
        state.coaching,
        `simulations.transcripts.[${childLearningRecord}].item`
      );
    });

    const simulationLearningRecord = useSelector((state: any) => {
      return _.get(state.coaching, `simulations.learnerReport.item`);
    });

    const currentAttempt = attempts.find((a) => a.id === selectedAttempt?.id) || selectedAttempt;

    const isReportLoaded = !!selectedSession?.report;

    const assessment = useSelector((state: any) => {
      if (!selectedSession) return null;
    
      const contents = _.get(
        state,
        `courses.learnerReport.${learningId}.item.course.contents`,
        []
      );
    
      const allContents = flattenContents(contents);
          return allContents.find(
        (content: any) =>
          content.entityType === "assessment" &&
          (content.id === selectedSession.id || content.session === selectedSession.session)
      );
    });

    const handleOpenAssessment = (attempt: any, session: any) => {
      if (!attempt || !session) {
        console.warn("Attempt or session is undefined", { attempt, session });
        return;
      }
    
      setSelectedAttempt(attempt);
      setSelectedSessionId(session.id);
      setShowReport(true);
    };

    const handleCloseReport = () => {
      setShowReport(false);
    };

    const openSimulationReport = (
      { attempt, session }: { attempt: SimulationAttempt; session: SimulationSession }
    ) => {
      setSimulationReport({ attempt, session });
    };

   const closeSimulationReport = () => {
     setSimulationReport(null);
   };

   const openCallRecording = (
    { attempt, session }: { attempt: SimulationAttempt; session?: SimulationSession }
  ) => {
    if (!attempt) return;
  
    setCallRecording({
      attempt,
      session,
      loading: true,
    });
  };

   const closeCallRecording = () => {
     setCallRecording(null);
   };
  
    const buildRequest = useCallback(
      (page: number): FetchCourseLearnersRequest => ({
        course: courseId,
        status: "COMPLETED",
        page,
        limit: 10,
        userGroups: selectedUserGroups.length
          ? selectedUserGroups.map((group) => group.id)
          : undefined,
      }),
      [courseId, selectedUserGroups],
    );

    const fetchLearners = useCallback(
      (page: number) => {
        if (courseId) {
          dispatch(fetchCourseLearners(buildRequest(page)));
        }
      },
      [buildRequest, courseId, dispatch],
    );

    const handleLoadMoreLearners = () => {
      if (nextPage !== null) {
        fetchLearners(nextPage);
      }
    };

    const handleUserGroupsChange = (groups: UserGroup[]) => {
      setSelectedUserGroups(groups);
    };

    const handleBack = () => {
      navigateManageLearnerReportsBack(navigate, {
        from: navigationFrom,
        entityType: "course",
        entityId: courseId!,
        fallback: userRoleService.canAccessCoachingReport()
          ? COACHING_REPORTS_PATH
          : undefined,
        selectedUserGroups,
        navigationState: location.state,
      });
    };

   const [courseContext, setCourseContext] =
     React.useState<CourseContextProps>({
       callRecording,
       openCallRecording,
       closeCallRecording,
       simulationReport,
       openSimulationReport,
       closeSimulationReport,
       showAssetsDialog: false,
       setShowAssetDialog: () => { },
       gotoError: () => { },
       simulationLearningRecord
     });

     useEffect(() => {
      setCourseContext(prev => ({
        ...prev,
        callRecording,
        simulationReport,
        simulationLearningRecord
      }));
    }, [callRecording, simulationReport,simulationLearningRecord]);
  
    const catalogCourse = useSelector((state: any) =>
      _.get(state, `courses.course[${courseId}].item`),
    );

    useEffect(() => {
      if (!courseId) {
        return;
      }

      if (userRoleService.canCreateCourse()) {
        if (!catalogCourse) {
          dispatch(fetchDraftCourse(courseId));
        }
      } else if (!catalogCourse) {
        dispatch(fetchPublishedCourse(courseId));
      }
    }, [catalogCourse, courseId, dispatch]);

    useEffect(() => {
      fetchLearners(0);
    }, [fetchLearners]);

    useEffect(() => {
      if (learnersLoading || !learnersLoaded) {
        return;
      }

      if (completedLearners.length === 0) {
        if (learningId && courseId) {
          navigate(`/coaching/manage/courses/${courseId}/reports`, {
            state: {
              ...(location.state as object),
              ...(selectedUserGroups.length ? { selectedUserGroups } : {}),
            },
            replace: true,
          });
        }
        return;
      }

      const currentInList = completedLearners.some(
        (item: { id: string }) => item.id === learningId,
      );

      if (currentInList) {
        return;
      }

      navigate(
        `/coaching/manage/courses/${courseId}/reports/${completedLearners[0].id}`,
        {
          state: {
            ...(location.state as object),
            ...(selectedUserGroups.length ? { selectedUserGroups } : {}),
          },
        },
      );
    }, [
      completedLearners,
      courseId,
      learningId,
      learnersLoaded,
      learnersLoading,
      navigate,
      navigationFrom,
      selectedUserGroups,
    ]);

    useEffect(() => {
      if (!childLearningRecord) return;
    
      dispatch(fetchTranscriptsForSimulation(childLearningRecord));
    }, [childLearningRecord]);

    useEffect(() => {
      if (!callRecording) return;
    
      setCallRecording(prev => {
        if (!prev) return null;
    
        return {
          ...prev,
          loading: fetchingTranscripts,
          transcripts,
        };
      });
    }, [fetchingTranscripts, transcripts, callRecording]);
  
   return (
     <CourseContext.Provider
       value={courseContext}>
       <div
         className={cx(styles.reportsContainer, {
           [styles.sidebarCollapsed]: showReport || callRecording,
         })}
       >
         <div className={styles.header}>
           <div className={styles.backContainer}>
             <div
               className={styles.back}
               onClick={handleBack}
             >
               <i className="paperflite-left-arrow"></i>
               <span>{getManageReportsBackLabel("course")}</span>
             </div>
           </div>
         </div>

         <div className={styles.bodyWrapper}>
           <div className={styles.left}>
             <div className={styles.title}>
               completed learners ({totalRecordsCount || _.size(learners)})
             </div>
             <div className={styles.filtersRow}>
               <UserGroupsFilter
                 selectedGroups={selectedUserGroups}
                 onChange={handleUserGroupsChange}
               />
             </div>
             <div
               className={cx(styles.contentsList, {
                 [styles.slideOut]: !showReport,
               })}
             >
               <InfiniteScroll
                 className={styles.scroll}
                 loading={learnersLoading}
                 hasMore={hasMoreItems}
                 loadMore={handleLoadMoreLearners}
                 loader={() => {
                   if (showReportsEmptyState) {
                     return null;
                   }

                   return _.times(
                     _.size(learners) === 0 || currentPage === 0 ? 7 : 1,
                     (index) => <LearnerPlaceholder key={index} />,
                   );
                 }}
               >
                 {showReportsEmptyState && (
                   <div className={styles.emptyListState} role="status">
                     {_.times(3, (index) => (
                       <LearnerPlaceholder key={index} disableShimmer />
                     ))}
                   </div>
                 )}

                 {!showReportsEmptyState && (
                   <CoursesLearnersList selectedUserGroups={selectedUserGroups} />
                 )}
               </InfiniteScroll>
             </div>
           </div>
           <div className={styles.right}>
             <div className={styles.detailContainer}>
               {(loading || learnersCourseRecord) &&
                 !learner &&
                 !showReportsEmptyState && <CourseReportPlaceholder />}
               {showReportsEmptyState && (
                 <div className={styles.emptyMessage} role="status">
                   <h2 className={styles.emptyTitle}>no learners found</h2>
                   <p className={styles.emptySubText}>{reportsEmptyDescription}</p>
                 </div>
               )}
               {learner && (
                 <EditCourseReportsDetail
                   learner={learner}
                   onOpenAssessment={handleOpenAssessment}
                   showReport={showReport}
                   handleCloseReport={handleCloseReport}
                   initialAttempt={initialAttempt}
                 />
               )}
               <div className={styles.simulationOverlayWrapper}>
                 <div
                   className={cx(styles.simulationOverlay, {
                     [styles.simulationOverlayVisible]: !!simulationReport,
                   })}
                 >
                    <SimulationReportView learner={learner} />
                 </div>
               </div>
             </div>
           </div>

           {showReport && (
             <div className={styles.assessmentWrapper}>
               <AssessmentReportView
                 course={course}
                 assessment={assessment}
                 report={selectedSession?.report}
                 isReportLoaded={isReportLoaded}
                 currentAttempt={currentAttempt?.attemptNumber}
                 onEndAssessment={() => {
                   dispatch(
                     invalidateLearnersAssesmentReport({
                       learningRecord: learningId!,
                       sessionId: selectedSession?.id
                     })
                   );
                   setShowReport(false);
                 }}
               />
             </div>
           )}

           {callRecording && (
             <div className={styles.callRecordingWrapper}>
               <CallRecording learner={learner} />
             </div>
           )}

         </div>
       </div>
     </CourseContext.Provider>
   );
  };
  
  export const LearnerPlaceholder: React.FC<{ disableShimmer?: boolean }> = ({ disableShimmer = false }) => {
    return (
      <div className={cx(styles.stacks, styles.placeholder)}>
        <div className={cx(styles.name, !disableShimmer && styles.block)}></div>
        <div className={styles.info}>
          <div className={cx(!disableShimmer && styles.block, styles.blockName)}></div>
          <div className={cx(!disableShimmer && styles.block, styles.blockDescription)}></div>
        </div>
      </div>
    );
  };
  
  export const CourseReportPlaceholder: React.FC = () => {
    return (
      <div className={cx(styles.detailContainer, styles.placeholder)}>
        <div className={styles.headerContainer}>
          <div className={cx(styles.name, styles.block)}></div>
          <div className={styles.info}>
            <div className={cx(styles.title, styles.block)}></div>
            <div className={cx(styles.subTitle, styles.block)}></div>
        </div>
        </div>
        <div className={styles.content}>
          <div className={styles.card}></div>
          <div className={styles.card}></div>
          <div className={styles.card}></div>
        </div>
      </div>
    );
  };

export default EditCoursesReportsPage;